import type { RestaurantTable } from '@/lib/api/tables';

type NumericLike = number | string | null | undefined;

export interface TableQueueMeta {
  baseOrder: number;
  fallbackIndex: number;
  isActive: boolean;
}

export interface TableQueueOrderingResult {
  orderedTables: RestaurantTable[];
  metaById: Map<string, TableQueueMeta>;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const toNumericValue = (value: NumericLike): number | null => {
  if (isFiniteNumber(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

export const computeBaseOrder = (table: RestaurantTable, index: number): number => {
  if (isFiniteNumber(table.displayOrder)) {
    return table.displayOrder;
  }

  const numericTableNumber = toNumericValue(table.tableNumber);
  if (numericTableNumber !== null) {
    return numericTableNumber;
  }

  return index;
};

export const isTableActive = (table: RestaurantTable): boolean => {
  if (table.status && table.status !== 'OPEN') {
    return true;
  }

  if (table.hasPendingCash) {
    return true;
  }

  if ((table.pendingCashOrderCount ?? 0) > 0) {
    return true;
  }

  if ((table.pendingCashTotalCents ?? 0) > 0) {
    return true;
  }

  if (Array.isArray(table.pendingCashOrders) && table.pendingCashOrders.length > 0) {
    return true;
  }

  return false;
};

const compareByBaseOrder = (
  a: { meta: TableQueueMeta },
  b: { meta: TableQueueMeta },
): number => {
  if (a.meta.baseOrder !== b.meta.baseOrder) {
    return a.meta.baseOrder - b.meta.baseOrder;
  }
  return a.meta.fallbackIndex - b.meta.fallbackIndex;
};

export const buildTableQueueOrdering = (
  tables: RestaurantTable[],
): TableQueueOrderingResult => {
  const metaById = new Map<string, TableQueueMeta>();

  const annotated = tables.map((table, index) => {
    const meta: TableQueueMeta = {
      baseOrder: computeBaseOrder(table, index),
      fallbackIndex: index,
      isActive: isTableActive(table),
    };
    metaById.set(table.id, meta);
    return { table, meta };
  });

  const active: typeof annotated = [];
  const idle: typeof annotated = [];

  annotated.forEach((entry) => {
    if (entry.meta.isActive) {
      active.push(entry);
      return;
    }
    idle.push(entry);
  });

  active.sort(compareByBaseOrder);
  idle.sort(compareByBaseOrder);

  return {
    orderedTables: [...active, ...idle].map((entry) => entry.table),
    metaById,
  };
};

